import { Icon, Stack } from '@chakra-ui/react';
import React, { useContext, useEffect } from 'react';
import { FaDollarSign } from 'react-icons/fa';

import {
  FiBarChart,
  FiFileText,
  FiFolder,
  FiHome,
  FiMail,
  FiMessageCircle,
  FiSettings,
  FiSliders,
} from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { AiOutlineAlert, TbMessages } from 'react-icons/all';
import { AiOutlineFund } from 'react-icons/ai';
import { ROUTER_PATHS } from '../../constants';
import {
  useCan,
  useIsComissioned,
  useRoleCan,
  useViewCommission,
} from '../../hooks/useCan';
import { useSidebar } from '../../hooks/useSidebar';
import { Dropdown } from './Dropdown';
import { NavLink } from './NavLink';
import { useFeatureFlags } from '~/contexts/FeatureFlagsContext/FeatureFlagsContext';
import { useDashboardsAvailable } from '~/hooks/useDashboards';
import { replaceParameters } from '~/utils/replaceParamaters';

export const Navbar: React.VFC = () => {
  const { isOpen, onToggle } = useSidebar();
  const history = useHistory();
  const userIsAdmin = useCan({ accessTypes: ['ADMIN'] });
  const userIsAdminRole = useRoleCan({ roles: ['administrador'] });
  const userIsOperacional = useRoleCan({ roles: ['operacional'] });
  const userIsTechinician = useRoleCan({ roles: ['técnico'] });
  const userIsMasterClient = useRoleCan({ roles: ['cliente master'] });
  const isComissioned = useIsComissioned();
  const viewCommission = useViewCommission();
  const features = useFeatureFlags();
  const { data, isLoading, refetch } = useDashboardsAvailable();
  const commercialManager =
    useRoleCan({
      roles: ['gerente de contas'],
    }) &&
    (isComissioned || viewCommission);
  const commercialPartner = useRoleCan({ roles: ['parceiro comercial'] });
  const businessInformationAllow =
    userIsAdmin || commercialManager || commercialPartner;
  const userCanSeeSinisters = useRoleCan({
    roles: ['administrador', 'cliente', 'técnico'],
  });
  const userCanSeeInsurances = useRoleCan({
    roles: ['administrador', 'operacional', 'suporte'],
  });
  const userCanSeeStrategicMap = useRoleCan({
    roles: [
      'administrador',
      'operacional',
      'suporte',
      'técnico',
      'gerente de contas',
    ],
  });
  // TODO: declarar itens do menu por funcao igual as rotas

  return (
    <Stack gridGap={4} width="96%">
      {!userIsTechinician && (
        <NavLink
          icon={FiHome}
          to={ROUTER_PATHS.PORTAL.HOME}
          shouldMatchExactHref
        >
          Início
        </NavLink>
      )}
      {!userIsTechinician && features.solicitarRescisaoContratosEnabled && (
        <>
          {isOpen ? (
            <Dropdown icon={FiFileText} label="Contratos">
              <NavLink to={ROUTER_PATHS.PORTAL.CONTRACTS}>Contratos</NavLink>
              <NavLink
                to={ROUTER_PATHS.PORTAL.REQUEST_CONTRACT_TERMINATION_LIST}
              >
                Solicitações de rescisão
              </NavLink>
            </Dropdown>
          ) : (
            <Icon
              as={FiFileText}
              fontSize="20px"
              cursor="pointer"
              onClick={onToggle}
              color="gray.700"
              title="Contratos"
            />
          )}
        </>
      )}
      {!userIsTechinician && !features.solicitarRescisaoContratosEnabled && (
        <>
          {isOpen ? (
            <NavLink icon={FiFileText} to={ROUTER_PATHS.PORTAL.CONTRACTS}>
              Contratos
            </NavLink>
          ) : (
            <Icon
              as={FiFileText}
              fontSize="20px"
              cursor="pointer"
              onClick={onToggle}
              color="gray.700"
              title="Contratos"
            />
          )}
        </>
      )}
      {!userIsTechinician && (
        <NavLink
          to={ROUTER_PATHS.PORTAL.INVOICES}
          icon={FaDollarSign}
          color="#78797A"
        >
          Faturas
        </NavLink>
      )}
      {features.mapaEstrategicoEnabled && userCanSeeStrategicMap && (
        <NavLink
          to={ROUTER_PATHS.PORTAL.STRATEGIC_MAP}
          icon={AiOutlineFund}
          color="#78797A"
        >
          Mapa Estratégico
        </NavLink>
      )}
      {!userIsTechinician && !userIsOperacional && (
        <NavLink
          to={ROUTER_PATHS.PORTAL.DOCUMENTS}
          icon={FiFolder}
          color="#78797A"
        >
          Documentos
        </NavLink>
      )}
      {userCanSeeSinisters && (
        <NavLink
          to={ROUTER_PATHS.PORTAL.SINISTERS}
          icon={AiOutlineAlert}
          color="#78797A"
        >
          Sinistros
        </NavLink>
      )}
      {/* ajustar exibir info comercial só pra qm tem acesso */}
      {isOpen ? (
        <Dropdown icon={FiBarChart} label="Relatórios">
          {businessInformationAllow && (
            <NavLink to={ROUTER_PATHS.PORTAL.BASE} disabled>
              Base instalada
            </NavLink>
          )}
          {businessInformationAllow && (
            <NavLink to={ROUTER_PATHS.PORTAL.BUSINESS_INFORMATION}>
              Informações Comerciais
            </NavLink>
          )}
          <NavLink to={ROUTER_PATHS.PORTAL.CONTRACT_MEASUREMENT}>
            Medição de Contratos
          </NavLink>
          {userCanSeeInsurances && (
            <NavLink to={ROUTER_PATHS.PORTAL.INSURANCE}>Seguros</NavLink>
          )}
          {features.dashboardsPowerBiEmbeddedEnabled &&
            data &&
            data?.length > 0 &&
            data?.map((dashboard, index) => (
              <NavLink
                to={replaceParameters(
                  ROUTER_PATHS.PORTAL.POWERBI_REPORT,
                  String(dashboard.id),
                )}
              >
                {dashboard.nome_relatorio}
              </NavLink>
            ))}
        </Dropdown>
      ) : (
        <Icon
          as={FiBarChart}
          fontSize="20px"
          cursor="pointer"
          color="gray.700"
          onClick={onToggle}
          title="Relatórios"
        />
      )}
      {!userIsOperacional &&
        !userIsTechinician &&
        !userIsMasterClient &&
        userIsAdmin && (
          <>
            {isOpen ? (
              <Dropdown icon={FiSliders} label="Administração">
                <NavLink to={ROUTER_PATHS.PORTAL.ADMIN.USERS}>Usuários</NavLink>
                <NavLink
                  to={ROUTER_PATHS.PORTAL.ADMIN.USERS_FORM}
                  disabled={history.location.pathname.startsWith(
                    ROUTER_PATHS.PORTAL.ADMIN.USERS_FORM,
                  )}
                >
                  Cadastro de usuários
                </NavLink>
                <NavLink to={ROUTER_PATHS.PORTAL.ADMIN.GROUPS}>Grupos</NavLink>
                <NavLink to={ROUTER_PATHS.PORTAL.ADMIN.COMPANIES}>
                  Empresas
                </NavLink>
                <NavLink to={ROUTER_PATHS.PORTAL.ADMIN.EQUIPMENT_TYPES}>
                  Equipamentos
                </NavLink>
                <NavLink to={ROUTER_PATHS.PORTAL.ADMIN.ACCESS_REPORTS}>
                  Relatórios de Acessos
                </NavLink>
                {userIsAdminRole && (
                  <NavLink to={ROUTER_PATHS.PORTAL.ADMIN.ANNOUNCEMENTS}>
                    Comunicados
                  </NavLink>
                )}
                {userIsAdminRole && (
                  <NavLink to={ROUTER_PATHS.PORTAL.ADMIN.PRIVACYPOLICY}>
                    Termos de Uso
                  </NavLink>
                )}
                {userIsAdminRole && (
                  <Dropdown label="Zero Carbon">
                    <NavLink
                      to={ROUTER_PATHS.PORTAL.ADMIN.ZERO_CARBON_PROJECTS}
                    >
                      Projetos
                    </NavLink>
                    <NavLink to={ROUTER_PATHS.PORTAL.ADMIN.ZERO_CARBON_LETTERS}>
                      Carta
                    </NavLink>
                  </Dropdown>
                )}
                {userIsAdminRole &&
                  features.crudDashboardsPowerBiEmbeddedEnabled && (
                    <NavLink to={ROUTER_PATHS.PORTAL.ADMIN.DASHBOARDS}>
                      Cadastro de Dashboards
                    </NavLink>
                  )}
              </Dropdown>
            ) : (
              <Icon
                as={FiSliders}
                fontSize="20px"
                cursor="pointer"
                onClick={onToggle}
                color="gray.700"
                title="Administração"
              />
            )}
          </>
        )}
      {/* Menu Administração usuário cliente master */}
      {userIsMasterClient && (
        <>
          {isOpen ? (
            <Dropdown icon={FiSliders} label="Administração">
              <NavLink to={ROUTER_PATHS.PORTAL.ADMIN.USERS}>Usuários</NavLink>
            </Dropdown>
          ) : (
            <Icon
              as={FiSliders}
              fontSize="20px"
              cursor="pointer"
              onClick={onToggle}
              color="gray.700"
              title="Administração"
            />
          )}
        </>
      )}
      {!userIsOperacional && !userIsTechinician && userIsAdmin ? (
        <>
          {isOpen ? (
            <Dropdown icon={FiSettings} label="Configurações">
              <NavLink to={ROUTER_PATHS.PORTAL.PROFILE}>Perfil</NavLink>
              <NavLink to={ROUTER_PATHS.PORTAL.ADMIN.NON_PAYMENT}>
                Inadimplência
              </NavLink>
              <NavLink to={ROUTER_PATHS.PORTAL.EMAILS}>E-mails</NavLink>
              <NavLink to={ROUTER_PATHS.PORTAL.ADMIN.DISCOUNT}>
                Descontos
              </NavLink>
              {features.parametrosAnaliseExtensaoAutomaticaEnabled && (
                <NavLink to={ROUTER_PATHS.PORTAL.ADMIN.AUTOMATIC_ANALYSIS}>
                  Análise Automática
                </NavLink>
              )}
            </Dropdown>
          ) : (
            <Icon
              as={FiSettings}
              fontSize="20px"
              cursor="pointer"
              onClick={onToggle}
              color="gray.700"
              title="Configurações"
            />
          )}
        </>
      ) : (
        <NavLink icon={FiSettings} to={ROUTER_PATHS.PORTAL.PROFILE}>
          Perfil
        </NavLink>
      )}
      {!userIsOperacional && !userIsTechinician && (
        <>
          {isOpen ? (
            <Dropdown icon={TbMessages} label="Suporte Técnico">
              <NavLink to={ROUTER_PATHS.PORTAL.TECHNICAL_SUPPORT}>
                Suporte Técnico
              </NavLink>
              <NavLink to={ROUTER_PATHS.PORTAL.OCCURRENCES}>Chamados</NavLink>
            </Dropdown>
          ) : (
            <Icon
              as={TbMessages}
              fontSize="20px"
              cursor="pointer"
              onClick={onToggle}
              color="gray.700"
              title="Suporte Técnico"
            />
          )}
        </>
      )}
      {!userIsAdminRole && !userIsOperacional && (
        <NavLink icon={FiMail} to={ROUTER_PATHS.PORTAL.ANNOUNCEMENTS}>
          Comunicados
        </NavLink>
      )}
      <NavLink icon={FiMessageCircle} to={ROUTER_PATHS.PORTAL.CONTACT}>
        Fale Conosco
      </NavLink>
    </Stack>
  );
};
